import { useState } from 'react';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { abnahmeApi, type KriteriumPruefungDto, type PruefAnleitungDto } from '../../lib/api/abnahme';
import PruefAnleitung from './PruefAnleitung';

const datum = (iso: string | null): string => {
  if (!iso) return '';
  const wert = new Date(iso);
  return Number.isNaN(wert.getTime()) ? '' : wert.toLocaleDateString('de-DE');
};

/** Woher der Stand kommt, in einem Halbsatz — Tests und Handprüfung getrennt. */
function quelle(pruefung: KriteriumPruefungDto | undefined): string {
  if (!pruefung || pruefung.status === 'keine') return 'noch nicht geprüft';
  const teile: string[] = [];
  if (pruefung.automatisch > 0) {
    const tests = `${pruefung.automatisch} ${pruefung.automatisch === 1 ? 'automatischer Test' : 'automatische Tests'}`;
    teile.push(pruefung.automatischRot > 0 ? `${tests}, ${pruefung.automatischRot} rot` : tests);
  }
  if (pruefung.manuell) {
    const wer = pruefung.manuell.von ? ` von ${pruefung.manuell.von}` : '';
    const wann = pruefung.manuell.am ? ` am ${datum(pruefung.manuell.am)}` : '';
    const wie = pruefung.manuell.status === 'fehlgeschlagen' ? 'von Hand als nicht ok markiert' : 'von Hand geprüft';
    teile.push(`${wie}${wer}${wann}`);
  }
  return teile.join(' · ');
}

function StatusSymbol({ status }: { status: KriteriumPruefungDto['status'] | undefined }) {
  if (status === 'gruen') {
    return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label="geprüft" />;
  }
  if (status === 'rot') {
    return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-label="nicht ok" />;
  }
  return <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-label="offen" />;
}

/**
 * Akzeptanzkriterien einer Story mit ihrem Prüfstand — und, wer darf, zum Abhaken.
 *
 * „OK“ und „Nicht OK“ landen als manuelles Testergebnis (docs/abnahme-plan.md,
 * Abschnitt 12): Sie zählen im Board, in der Freigabe und in der Statistik genau
 * wie ein manueller Testlauf. Ein fehlgeschlagener automatischer Test bleibt rot,
 * auch wenn jemand von Hand „OK“ sagt.
 */
export default function KriterienPruefung({
  kriterien,
  darfPruefen,
  onGeprueft,
}: {
  kriterien: Array<{
    key: string;
    text: string;
    anleitung?: PruefAnleitungDto | null;
    pruefung?: KriteriumPruefungDto;
  }>;
  darfPruefen: boolean;
  onGeprueft: () => void | Promise<void>;
}) {
  const [arbeitet, setArbeitet] = useState<string | null>(null);
  const [nichtOk, setNichtOk] = useState<{ key: string; meldung: string; ticket: boolean } | null>(null);

  const pruefen = async (key: string, status: 'bestanden' | 'fehlgeschlagen', meldung?: string, ticket?: boolean) => {
    setArbeitet(key);
    try {
      const ergebnis = await abnahmeApi.kriteriumPruefen(key, {
        status,
        meldung: meldung?.trim() || undefined,
        ticketAnlegen: ticket ?? false,
      });
      toast.success(
        status === 'bestanden'
          ? `${key} als geprüft markiert.`
          : `${key} als nicht ok markiert${ergebnis.ticketRef ? ` — Ticket ${ergebnis.ticketRef} angelegt` : ''}.`,
      );
      setNichtOk(null);
      await onGeprueft();
    } catch (fehler) {
      toast.error(fehler instanceof Error ? fehler.message : 'Das Ergebnis wurde nicht gespeichert.');
    } finally {
      setArbeitet(null);
    }
  };

  return (
    <ul className="space-y-2 text-sm">
      {kriterien.map((kriterium) => {
        const status = kriterium.pruefung?.status;
        const offen = nichtOk?.key === kriterium.key;
        return (
          <li key={kriterium.key} className="flex gap-2">
            <StatusSymbol status={status} />
            <div className="min-w-0 flex-1 space-y-1">
              <div>{kriterium.text}</div>
              <PruefAnleitung anleitung={kriterium.anleitung} />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="font-mono">{kriterium.key}</span>
                <span>{quelle(kriterium.pruefung)}</span>
                {kriterium.pruefung?.manuell?.status === 'fehlgeschlagen' && kriterium.pruefung.manuell.meldung && (
                  <span className="text-red-700 dark:text-red-400">„{kriterium.pruefung.manuell.meldung}“</span>
                )}
                {darfPruefen && !offen && (
                  <span className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      disabled={arbeitet !== null}
                      onClick={() => void pruefen(kriterium.key, 'bestanden')}
                    >
                      {arbeitet === kriterium.key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'OK'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      disabled={arbeitet !== null}
                      onClick={() => setNichtOk({ key: kriterium.key, meldung: '', ticket: true })}
                    >
                      Nicht OK …
                    </Button>
                  </span>
                )}
              </div>
              {offen && nichtOk && (
                <div className="space-y-2 rounded-md border p-3">
                  <Label htmlFor={`nicht-ok-${kriterium.key}`}>Was stimmt nicht?</Label>
                  <Textarea
                    id={`nicht-ok-${kriterium.key}`}
                    rows={2}
                    value={nichtOk.meldung}
                    onChange={(e) => setNichtOk({ ...nichtOk, meldung: e.target.value })}
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={nichtOk.ticket}
                      onChange={(e) => setNichtOk({ ...nichtOk, ticket: e.target.checked })}
                    />
                    Ticket anlegen
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={arbeitet !== null || !nichtOk.meldung.trim()}
                      onClick={() => void pruefen(kriterium.key, 'fehlgeschlagen', nichtOk.meldung, nichtOk.ticket)}
                    >
                      Als nicht ok speichern
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setNichtOk(null)}>
                      Abbrechen
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
